/**
 * Maintenance calendar: PM schedules, certificate expiries and scheduled work
 * orders on a month or week grid, with an agenda list for narrow screens.
 */
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import {
  addDays,
  addMonths,
  addWeeks,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  parseISO,
  startOfMonth,
  startOfWeek,
  startOfDay,
} from "date-fns";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { type ReactElement, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { api } from "@/api/client";
import { unwrap } from "@/api/problem";
import { useAuth } from "@/app/auth";
import { AssetReference } from "@/features/entities/AssetReference";
import { useGenerateWorkOrders, useRebaseSchedule } from "@/features/admin/maintenance/queries";
import {
  ScheduleDueDetails,
  scheduleStatus,
  type Schedule,
  useToggleSchedule,
} from "@/features/maintenance/MaintenancePage";
import { FormDialog } from "@/shared/components/form-dialog";
import { ErrorState, LoadingState, PageHeader, StatusBadge } from "@/shared/components/page";
import { cn, formatDateTime } from "@/shared/lib/format";
import { isoOrNull, numberOrNull, useForm } from "@/shared/lib/form";
import { collectPages } from "@/shared/lib/paging";
import { Button } from "@/shared/ui/button";
import { Checkbox, Field, Input } from "@/shared/ui/form";
import { Badge, Dialog } from "@/shared/ui/primitives";

/** A dated item rendered on the calendar. */
export interface CalendarEvent {
  id: string;
  date: Date;
  kind: "schedule" | "certificate" | "workorder";
  title: string;
  subtitle: string;
  status: string;
  href?: string;
  schedule?: Schedule;
}

/** Visible range for a view. */
export function rangeFor(view: "month" | "week", anchor: Date): { start: Date; end: Date } {
  if (view === "week") {
    return {
      start: startOfWeek(anchor, { weekStartsOn: 1 }),
      end: endOfWeek(anchor, { weekStartsOn: 1 }),
    };
  }
  return {
    start: startOfWeek(startOfMonth(anchor), { weekStartsOn: 1 }),
    end: endOfWeek(endOfMonth(anchor), { weekStartsOn: 1 }),
  };
}

/** Days covering the range (inclusive). */
export function daysIn(range: { start: Date; end: Date }): Date[] {
  const out: Date[] = [];
  for (let d = range.start; d <= range.end; d = addDays(d, 1)) {
    out.push(d);
  }
  return out;
}

/** Load calendar sources for a range. */
function useCalendarEvents(
  range: { start: Date; end: Date },
  show: { schedules: boolean; certificates: boolean; workorders: boolean },
): { events: CalendarEvent[]; isPending: boolean; error: Error | null } {
  const { can } = useAuth();
  const { t } = useTranslation();
  const after = range.start.toISOString();
  const before = range.end.toISOString();
  const schedules = useQuery({
    queryKey: ["schedules", "calendar", after, before],
    queryFn: () =>
      collectPages(async (cursor) =>
        unwrap(
          await api.GET("/api/v1/maintenance/schedules", {
            params: { query: { due_after: after, due_before: before, limit: 200, cursor } },
          }),
        ),
      ),
    enabled: show.schedules && can("maintenance.plan.view"),
  });
  const certificates = useQuery({
    queryKey: ["certificates", "calendar", after, before],
    queryFn: () =>
      collectPages(async (cursor) =>
        unwrap(
          await api.GET("/api/v1/assets/certificates", {
            params: { query: { limit: 500, cursor } },
          }),
        ),
      ),
    enabled: show.certificates && can("assets.certificate.view"),
  });
  const workorders = useQuery({
    queryKey: ["work-orders", "calendar", after, before],
    queryFn: () =>
      collectPages(async (cursor) =>
        unwrap(
          await api.GET("/api/v1/work-orders/", {
            params: {
              query: { scheduled_after: after, scheduled_before: before, limit: 200, cursor },
            },
          }),
        ),
      ),
    enabled:
      show.workorders &&
      (can("workorders.workorder.view_assigned") || can("workorders.workorder.view_all")),
  });
  const events = useMemo<CalendarEvent[]>(() => {
    const out: CalendarEvent[] = [];
    for (const s of show.schedules ? (schedules.data ?? []) : []) {
      const addScheduleEvent = (suffix: string, due: string | null, label: string): void => {
        if (!due) return;
        const date = parseISO(due);
        if (date < range.start || date > range.end) return;
        out.push({
          id: `s-${s.id}-${suffix}`,
          date,
          kind: "schedule",
          title: s.plan_name,
          subtitle: `${s.asset_code} · ${s.asset_name} · ${label}`,
          status: s.status !== "suspended" && s.is_due && !s.is_overdue ? "due" : scheduleStatus(s),
          schedule: s,
        });
      };
      addScheduleEvent("calendar", s.next_due_at, t("feedbackMaintenance.calendarDeadline"));
      if (s.meter_due) {
        addScheduleEvent("meter", s.meter_current_at, t("feedbackMaintenance.meterThreshold"));
      } else {
        addScheduleEvent(
          "projected-meter",
          s.projected_due_at,
          t("feedbackMaintenance.projectedDateEstimated"),
        );
      }
    }
    for (const c of show.certificates ? (certificates.data ?? []) : []) {
      if (c.expires_on) {
        const d = parseISO(c.expires_on);
        if (d >= range.start && d <= range.end) {
          out.push({
            id: `c-${c.id}`,
            date: d,
            kind: "certificate",
            title: c.name,
            subtitle: `${c.asset_code} · ${c.asset_name}`,
            status: c.status,
            href: `/assets/${c.asset_id}`,
          });
        }
      }
    }
    for (const w of show.workorders ? (workorders.data ?? []) : []) {
      const due = w.scheduled_start ?? w.due_at;
      if (due) {
        const d = parseISO(due);
        const end = w.scheduled_end ? parseISO(w.scheduled_end) : d;
        for (
          let day = startOfDay(d < range.start ? range.start : d);
          day <= end && day <= range.end;
          day = addDays(day, 1)
        ) {
          if (day < startOfDay(range.start) || day > range.end) continue;
          out.push({
            id: `w-${w.id}-${day.toISOString()}`,
            date: day,
            kind: "workorder",
            title: `${w.code} ${w.title}`,
            subtitle: w.asset.name,
            status: w.status,
            href: `/work-orders/${w.id}`,
          });
        }
      }
    }
    return out.sort((a, b) => a.date.getTime() - b.date.getTime());
  }, [schedules.data, certificates.data, workorders.data, range.start, range.end, show, t]);
  const isPending =
    (show.schedules && schedules.isPending && schedules.fetchStatus !== "idle") ||
    (show.certificates && certificates.isPending && certificates.fetchStatus !== "idle") ||
    (show.workorders && workorders.isPending && workorders.fetchStatus !== "idle");
  const error = schedules.error ?? certificates.error ?? workorders.error;
  return { events, isPending, error };
}

/** Tone class for an event chip. */
export function chipClass(ev: CalendarEvent): string {
  if (ev.kind === "certificate") {
    return ev.status === "expired" ? "bg-red-100 text-red-900" : "bg-amber-100 text-amber-900";
  }
  if (ev.kind === "workorder") {
    return "bg-blue-100 text-blue-900";
  }
  return ev.status === "overdue"
    ? "bg-red-100 text-red-900"
    : ev.status === "due"
      ? "bg-amber-100 text-amber-900"
      : ev.status === "suspended"
        ? "bg-muted text-muted-foreground"
        : "bg-emerald-100 text-emerald-900";
}

interface RebaseValues extends Record<string, unknown> {
  next_due_at: string;
  next_due_meter: string;
}

/** Schedule drawer: details + suspend/resume/rebase. */
function ScheduleDialog({
  schedule,
  onClose,
}: {
  schedule: Schedule;
  onClose: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const { can } = useAuth();
  const toggle = useToggleSchedule();
  const rebase = useRebaseSchedule();
  const [rebasing, setRebasing] = useState(false);
  const form = useForm<RebaseValues>({
    initial: {
      next_due_at: schedule.next_due_at?.slice(0, 16) ?? "",
      next_due_meter: schedule.next_due_meter ?? "",
    },
    idPrefix: "rebase",
  });
  const manage = can("maintenance.schedule.manage");
  const submitRebase = async (): Promise<void> => {
    const ok = await form.submit((v) =>
      rebase.mutateAsync({
        id: schedule.id,
        body: {
          next_due_at: isoOrNull(v.next_due_at),
          next_due_meter: numberOrNull(v.next_due_meter),
        },
      }),
    );
    if (ok) {
      onClose();
    }
  };
  if (rebasing) {
    return (
      <FormDialog
        open
        onOpenChange={(o) => !o && onClose()}
        title={t("calendar.rebase")}
        description={`${schedule.plan_name} · ${schedule.asset_code}`}
        onSubmit={submitRebase}
        submitting={form.submitting}
        error={form.errors._}
      >
        <Field id="rebase-next_due_at" label={t("maintenance.nextDue")}>
          <Input type="datetime-local" {...form.field("next_due_at")} />
        </Field>
        {schedule.next_due_meter !== null ? (
          <Field id="rebase-next_due_meter" label={t("calendar.nextDueMeter")}>
            <Input type="number" step="any" {...form.field("next_due_meter")} />
          </Field>
        ) : null}
      </FormDialog>
    );
  }
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={schedule.plan_name}
      description={`${schedule.asset_code} · ${schedule.asset_name}`}
      footer={
        manage ? (
          <>
            <Button variant="outline" onClick={() => setRebasing(true)}>
              {t("calendar.rebase")}
            </Button>
            <Button
              variant="outline"
              disabled={toggle.isPending}
              onClick={() =>
                toggle.mutate(
                  {
                    id: schedule.id,
                    action: schedule.status === "suspended" ? "resume" : "suspend",
                  },
                  { onSuccess: onClose },
                )
              }
            >
              {t(schedule.status === "suspended" ? "maintenance.resume" : "maintenance.suspend")}
            </Button>
          </>
        ) : null
      }
    >
      <dl className="grid grid-cols-2 gap-2 text-sm">
        <dt className="text-muted-foreground">{t("app.status")}</dt>
        <dd>
          <StatusBadge value={scheduleStatus(schedule)} namespace="maintenance.status" />
          {schedule.status !== "suspended" && schedule.is_due && !schedule.is_overdue ? (
            <Badge className="ms-1" tone="warning">
              {t("feedbackMaintenance.dueNow")}
            </Badge>
          ) : null}
        </dd>
        <div className="col-span-2">
          <ScheduleDueDetails schedule={schedule} />
        </div>
        <dt className="text-muted-foreground">{t("maintenance.lastCompleted")}</dt>
        <dd>{formatDateTime(schedule.last_completed_at)}</dd>
        <dt className="text-muted-foreground">{t("maintenance.compliance")}</dt>
        <dd>
          {schedule.compliance_count}/{schedule.total_count}
        </dd>
      </dl>
      <div className="flex gap-2 text-sm">
        <AssetReference
          id={schedule.asset_id}
          code={schedule.asset_code}
          name={schedule.asset_name}
          context="history"
        />
        {schedule.open_workorder_id ? (
          <Link
            to="/work-orders/$woId"
            params={{ woId: schedule.open_workorder_id }}
            className="text-primary hover:underline"
          >
            {t("maintenance.openWorkOrder")} →
          </Link>
        ) : null}
      </div>
    </Dialog>
  );
}

/** Calendar page. */
export function CalendarPage(): ReactElement {
  const { t, i18n } = useTranslation();
  const { can } = useAuth();
  const search = useSearch({ strict: false });
  const navigate = useNavigate();
  const view = search.tab === "week" ? "week" : "month";
  const anchor = useMemo(() => (search.q ? parseISO(search.q) : new Date()), [search.q]);
  const [show, setShow] = useState({ schedules: true, certificates: true, workorders: true });
  const [selected, setSelected] = useState<Schedule | null>(null);
  const [agendaDay, setAgendaDay] = useState<Date | null>(null);
  const range = useMemo(() => rangeFor(view, anchor), [view, anchor]);
  const days = useMemo(() => daysIn(range), [range]);
  const { events, isPending, error } = useCalendarEvents(range, show);
  const generate = useGenerateWorkOrders();

  const go = (patch: { tab?: string; q?: string }): void =>
    void navigate({ to: "/calendar", search: { ...search, ...patch } });
  const step = (dir: 1 | -1): void =>
    go({
      q: format(view === "week" ? addWeeks(anchor, dir) : addMonths(anchor, dir), "yyyy-MM-dd"),
    });
  const byDay = (d: Date): CalendarEvent[] => events.filter((e) => isSameDay(e.date, d));
  const weekdays = daysIn(rangeFor("week", anchor)).map((d) => format(d, "EEE"));

  const open = (ev: CalendarEvent): void => {
    if (ev.schedule) {
      setSelected(ev.schedule);
    } else if (ev.href) {
      void navigate({ to: ev.href });
    }
  };

  return (
    <div>
      <PageHeader
        title={t("calendar.title")}
        actions={
          <>
            {can("maintenance.schedule.manage") ? (
              <Button
                variant="outline"
                size="sm"
                disabled={generate.isPending}
                onClick={() => generate.mutate()}
              >
                {t("calendar.generateNow")}
              </Button>
            ) : null}
            <Button
              variant={view === "month" ? "default" : "outline"}
              size="sm"
              onClick={() => go({ tab: "month" })}
            >
              {t("calendar.month")}
            </Button>
            <Button
              variant={view === "week" ? "default" : "outline"}
              size="sm"
              onClick={() => go({ tab: "week" })}
            >
              {t("calendar.week")}
            </Button>
          </>
        }
      />
      {generate.isSuccess ? (
        <p role="status" className="mb-2 text-sm text-emerald-700">
          {t("calendar.generated", { count: generate.data.length })}
        </p>
      ) : null}
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <div className="flex flex-wrap items-center gap-1">
          <Button
            size="icon"
            variant="ghost"
            aria-label={t("calendar.previous")}
            onClick={() => step(-1)}
          >
            <ChevronLeft className="h-4 w-4 rtl:rotate-180" />
          </Button>
          <Button size="sm" variant="outline" onClick={() => go({ q: undefined })}>
            {t("calendar.today")}
          </Button>
          <Button
            size="icon"
            variant="ghost"
            aria-label={t("calendar.next")}
            onClick={() => step(1)}
          >
            <ChevronRight className="h-4 w-4 rtl:rotate-180" />
          </Button>
          <h2 className="ms-2 text-lg font-semibold">
            {view === "week"
              ? `${format(range.start, "d MMM")} – ${format(range.end, "d MMM yyyy")}`
              : format(anchor, "MMMM yyyy")}
          </h2>
        </div>
        <div className="ms-auto flex flex-wrap gap-3">
          <Checkbox
            id="cal-schedules"
            label={t("maintenance.schedules")}
            checked={show.schedules}
            onChange={(e) => setShow({ ...show, schedules: e.target.checked })}
          />
          <Checkbox
            id="cal-certificates"
            label={t("assets.certificates")}
            checked={show.certificates}
            onChange={(e) => setShow({ ...show, certificates: e.target.checked })}
          />
          <Checkbox
            id="cal-workorders"
            label={t("workOrders.title")}
            checked={show.workorders}
            onChange={(e) => setShow({ ...show, workorders: e.target.checked })}
          />
        </div>
      </div>
      {selected ? <ScheduleDialog schedule={selected} onClose={() => setSelected(null)} /> : null}
      {agendaDay ? (
        <Dialog
          open
          title={`${t("calendar.dayAgenda")} · ${format(agendaDay, "d MMM yyyy")}`}
          onOpenChange={(isOpen) => !isOpen && setAgendaDay(null)}
        >
          <ul className="space-y-2">
            {byDay(agendaDay).map((event) => (
              <li key={event.id}>
                <Button
                  variant="outline"
                  className="h-auto w-full justify-start whitespace-normal text-start"
                  onClick={() => {
                    setAgendaDay(null);
                    open(event);
                  }}
                >
                  {event.title} · {event.subtitle}
                </Button>
              </li>
            ))}
          </ul>
        </Dialog>
      ) : null}
      <Link to="/maintenance" className="mb-3 inline-block text-sm text-primary underline">
        {t("calendar.meterQueue")}
      </Link>
      {error ? <ErrorState error={error} /> : null}
      {isPending ? <LoadingState /> : null}

      {/* Grid (md+) */}
      <div className="hidden md:block" data-testid="calendar-grid">
        <div className="grid grid-cols-7 gap-px rounded-t-md border bg-border text-center text-xs font-medium uppercase text-muted-foreground">
          {weekdays.map((d) => (
            <div key={d} className="bg-muted/40 py-1">
              {d}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-px rounded-b-md border border-t-0 bg-border">
          {days.map((d) => {
            const items = byDay(d);
            const today = isSameDay(d, new Date());
            return (
              <div
                key={d.toISOString()}
                className={cn(
                  "min-h-24 bg-card p-1",
                  view === "month" &&
                    !isSameMonth(d, anchor) &&
                    "bg-muted/30 text-muted-foreground",
                )}
                data-date={format(d, "yyyy-MM-dd")}
              >
                <div
                  className={cn(
                    "mb-1 text-xs",
                    today && "inline-block rounded-full bg-primary px-1.5 text-primary-foreground",
                  )}
                >
                  {format(d, "d")}
                </div>
                <ul className="space-y-0.5">
                  {items.slice(0, view === "week" ? 20 : 4).map((ev) => (
                    <li key={ev.id}>
                      <button
                        type="button"
                        className={cn(
                          "w-full truncate rounded px-1 text-start text-[11px] leading-5 hover:opacity-80",
                          chipClass(ev),
                        )}
                        title={`${ev.title} — ${ev.subtitle}`}
                        onClick={() => open(ev)}
                      >
                        {ev.title}
                      </button>
                    </li>
                  ))}
                  {items.length > (view === "week" ? 20 : 4) ? (
                    <li>
                      <button
                        type="button"
                        className="text-[11px] text-primary underline"
                        onClick={() => setAgendaDay(d)}
                      >
                        {t("calendar.showAll", { count: items.length })}
                      </button>
                    </li>
                  ) : null}
                </ul>
              </div>
            );
          })}
        </div>
      </div>

      {/* Agenda (mobile) */}
      <ul className="space-y-2 md:hidden" data-testid="calendar-agenda">
        {events.length === 0 && !isPending ? (
          <li className="text-sm text-muted-foreground">{t("calendar.empty")}</li>
        ) : null}
        {events.map((ev) => (
          <li key={ev.id}>
            <button
              type="button"
              className="grid w-full grid-cols-[auto_minmax(0,1fr)] items-center gap-2 rounded-md border p-3 text-start"
              onClick={() => open(ev)}
            >
              <span className="text-xs text-muted-foreground">{format(ev.date, "d MMM")}</span>
              <span className={cn("justify-self-start rounded px-1 text-[11px]", chipClass(ev))}>
                {t(`calendar.kinds.${ev.kind}`)}
              </span>
              <span className="col-span-2 min-w-0 break-words text-sm">{ev.title}</span>
              <span className="col-span-2 min-w-0 break-words text-xs text-muted-foreground">
                {ev.subtitle}
              </span>
              <Badge tone="outline">{ev.status}</Badge>
            </button>
          </li>
        ))}
      </ul>
      <p className="mt-2 text-xs text-muted-foreground">
        {t("calendar.legend", { lng: i18n.language })}
      </p>
    </div>
  );
}
